/**
 * Infinite Scroll Hook
 * Detects when user scrolls near bottom and triggers load more callback
 */

import { useCallback, useEffect, useRef } from "react"
import { API } from "@/constants/constants"

export interface UseInfiniteScrollOptions {
  onLoadMore: () => void | Promise<void>
  hasMore: boolean
  threshold?: number
  disabled?: boolean
  root?: HTMLElement | null
}

export function useInfiniteScroll(options: UseInfiniteScrollOptions) {
  const {
    onLoadMore,
    hasMore,
    threshold = API.INFINITE_SCROLL_THRESHOLD_PX,
    disabled = false,
    root = null,
  } = options
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const callbackRef = useRef(onLoadMore)
  const hasMoreRef = useRef(hasMore)
  const disabledRef = useRef(disabled)

  // Keep refs in sync
  callbackRef.current = onLoadMore
  hasMoreRef.current = hasMore
  disabledRef.current = disabled

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0]
    if (target.isIntersecting && hasMoreRef.current && !disabledRef.current) {
      callbackRef.current()
    }
  }, [])

  useEffect(() => {
    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    const currentRoot = root
    if (!currentRoot) return

    const observerOptions = {
      root: currentRoot,
      rootMargin: `${threshold}px`,
      threshold: 0,
    }

    observerRef.current = new IntersectionObserver(handleObserver, observerOptions)

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observerRef.current.observe(currentRef)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [handleObserver, threshold, root])

  return loadMoreRef
}
